import { ReactElement } from 'react'
import { Dropdown } from 'react-bootstrap'
import { NavigateFunction } from 'react-router-dom'
import moment from 'moment'

import { prepareDataQA } from '../stringFormatters'
import { reCreateMarkupOnlyProject, reset3DProject, resetProject } from '../../api/projects-api'
import { completeMarkup, updateProjectByPortalNumber } from '../../api/takeoff-api'
import { IUserPermission } from '../../models/portal/permissions'
import { Project } from '../../models/project'
import { APP_NAMES } from '../../shared/constants/app-names'
import { DRAWABLE_TYPES } from '../../shared/constants/drawable-types'
import { PROJECT_ACTION_VISIBILITY } from '../../shared/constants/project-action-visibility'
import { PROJECT_ACTIONS } from '../../shared/constants/project-actions'
import { PROJECT_STATUSES_ENUM } from '../../shared/constants/project-list.constants'
import { PROJECT_TYPES_ENUM } from '../../shared/constants/project-type.constants'
import { PROJECT_STATUS_NAME } from '../../shared/constants/projectStatusName'
import { USER_PERMISSIONS_NAMES } from '../../shared/constants/user-permissions'

const actionPerformed = (
    action: string,
    project: Project,
    navigate: NavigateFunction,
    handleUpdateDataModalOpen: (isMerge: boolean) => void,
    handleAIMagicModalOpen: (id?: number) => void,
    handleRecalculateMeasurementsModalOpen?: (id: number) => void,
    setProject?: (project: Project | undefined) => void
): void => {
    switch (action) {
        case 'settings':
            navigate(`/project/${project.id}/settings`)
            break
        case 'takeoff':
            if (project?.projectStatus?.name === PROJECT_STATUS_NAME.AI_PROCESSING) handleAIMagicModalOpen()
            else navigate(`/project/${project.id}/takeoff`)
            break
        case 'markup':
            navigate(`/project/${project.id}/markup`)
            break
        case 'update_data':
            handleUpdateDataModalOpen(false)
            break
        case 'update_data_2d':
            resetProject(project.id, false, false)
            break
        case 'update_data_3d':
            reset3DProject(project.id)
            break
        case 'merge_data':
            handleUpdateDataModalOpen(true)
            break
        case 'windows_schedule_itemized':
            window.open(project.outputs?.scheduleURL ?? '', '_blank')
            break
        case 'windows_schedule_group':
            window.open(project.outputs?.groupedScheduleURL ?? '', '_blank')
            break
        case 'clear_project_errors':
            project.portal_number &&
                updateProjectByPortalNumber(
                    project.portal_number,
                    project.type,
                    PROJECT_STATUSES_ENUM.IN_PROGRESS,
                    PROJECT_STATUS_NAME.IN_PROGRESS,
                    setProject
                )
            break
        case 're_create_markup_only_project':
            project.portal_number && reCreateMarkupOnlyProject(project.portal_number)
            break
        case 'view_window_takeoff_csv':
            window.open(project.outputs?.csvUrl ?? '', '_blank')
            break
        case 'recalculate_measurements':
            handleRecalculateMeasurementsModalOpen && handleRecalculateMeasurementsModalOpen(project.id)
            break
        case 'generate_deliverables':
            completeMarkup(project.id, true)
            break
        default:
            break
    }
}

export const getProjectPDFActions = (
    project: Project,
    userPermissions: IUserPermission[] | null,
    handleUpdateDataModalOpen: () => void,
    handleAIMagicModalOpen: (id?: number) => void,
    navigate: NavigateFunction,
    handleRecalculateMeasurementsModalOpen?: (id?: number) => void
): {
    openItemizedScheduleAction?: () => void
    openGroupedScheduleAction?: () => void
} => {
    let openItemizedScheduleAction: (() => void) | undefined = undefined

    if (project.outputs?.scheduleURL) {
        openItemizedScheduleAction = getAction(
            project,
            userPermissions,
            'windows_schedule_itemized',
            handleUpdateDataModalOpen,
            handleAIMagicModalOpen,
            navigate,
            handleRecalculateMeasurementsModalOpen
        )
    }

    let openGroupedScheduleAction: (() => void) | undefined = undefined

    if (project.outputs?.groupedScheduleURL) {
        openGroupedScheduleAction = getAction(
            project,
            userPermissions,
            'windows_schedule_group',
            handleUpdateDataModalOpen,
            handleAIMagicModalOpen,
            navigate,
            handleRecalculateMeasurementsModalOpen
        )
    }

    return {
        openItemizedScheduleAction: openItemizedScheduleAction,
        openGroupedScheduleAction: openGroupedScheduleAction,
    }
}

function getAction(
    project: Project,
    userPermissions: IUserPermission[] | null,
    actionKey: string,
    handleUpdateDataModalOpen: (isMerge?: boolean) => void,
    handleAIMagicModalOpen: (id?: number) => void,
    navigate: NavigateFunction,
    handleRecalculateMeasurementsModalOpen?: (id?: number) => void
): (() => void) | undefined {
    const projectActions = PROJECT_ACTION_VISIBILITY[actionKey]

    if (!projectActions) {
        return
    }

    const userPermissionNames = projectActions.permissions.filter((permission) =>
        Object.values(USER_PERMISSIONS_NAMES).find((value) => value === permission)
    )

    if (
        projectActions?.projectStatuses?.includes(project?.projectStatus?.name) &&
        hasPermission(userPermissions, userPermissionNames) &&
        projectActions?.projectTypes.includes(project.type)
    ) {
        return (): void =>
            actionPerformed(
                actionKey,
                project,
                navigate,
                handleUpdateDataModalOpen,
                handleAIMagicModalOpen,
                handleRecalculateMeasurementsModalOpen
            )
    }
}

function projectCanBeRecreated(projectCreatedAt: string): boolean {
    const minutes = 15

    const projectCreatedAtTimestamp: number = moment.utc(projectCreatedAt).local().add(minutes, 'minutes').valueOf()

    const currentDate: Date = new Date()
    const currentTimestamp: number = currentDate.getTime()

    return projectCreatedAtTimestamp <= currentTimestamp
}

export const initActions = (
    project: Project,
    userPermissions: IUserPermission[] | null,
    isProjectList: boolean,
    handleUpdateDataModalOpen: (isMerge: boolean) => void,
    handleAIMagicModalOpen: (id?: number) => void,
    navigate: NavigateFunction,
    handleRecalculateMeasurementsModalOpen?: (id: number) => void,
    setProject?: (project: Project | undefined) => void
): ReactElement<any>[] => {
    const actions: ReactElement<any>[] = []

    PROJECT_ACTIONS.forEach((action: { label: string; hash: string }, index: number) => {
        const projectActions = PROJECT_ACTION_VISIBILITY[action.hash]

        if (projectActions) {
            const userPermissionNames = projectActions.permissions.filter((role) =>
                Object.values(USER_PERMISSIONS_NAMES).find((value) => value === role)
            )

            if (
                !projectActions?.projectStatuses?.includes(project?.projectStatus?.name) ||
                !hasPermission(userPermissions, userPermissionNames) ||
                !projectActions.projectTypes.includes(project.type)
            ) {
                return
            }
        }

        if (action.hash === 'windows_schedule_itemized' && !project.outputs?.scheduleURL) {
            return
        }

        if (action.hash === 'windows_schedule_group' && !project.outputs?.groupedScheduleURL) {
            return
        }

        if (action.hash === 'view_window_takeoff_csv' && !project.outputs?.csvUrl) {
            return
        }

        if (action.hash === 'assign') {
            return
        }

        if (action.hash === 'recalculate_measurements' && project.type !== PROJECT_TYPES_ENUM.INTERACTIVE_MARKUP_ONLY) {
            return
        }

        const isProjectCanBeRecreated: boolean = projectCanBeRecreated(project.created_at)

        if (action.hash === 're_create_markup_only_project' && !isProjectCanBeRecreated) {
            return
        }

        if (!isProjectList) {
            actions.push(
                <li
                    className="tool"
                    data-qa={`${prepareDataQA(action.hash)}-button`}
                    onClick={(): void =>
                        actionPerformed(
                            action.hash,
                            project,
                            navigate,
                            handleUpdateDataModalOpen,
                            handleAIMagicModalOpen,
                            handleRecalculateMeasurementsModalOpen,
                            setProject
                        )
                    }
                >
                    {action.label}
                </li>
            )
        } else {
            // Don't show update & merge Data actions when the project isn't out of sync or in an error state
            if (
                (action.hash === 'update_data' || action.hash === 'merge_data') &&
                project?.projectStatus?.name !== PROJECT_STATUS_NAME.ERROR
            ) {
                return
            }

            actions.push(
                <Dropdown.Item
                    data-qa={`${prepareDataQA(action.hash)}-button`}
                    key={index}
                    onClick={(): void =>
                        actionPerformed(
                            action.hash,
                            project,
                            navigate,
                            handleUpdateDataModalOpen,
                            handleAIMagicModalOpen,
                            handleRecalculateMeasurementsModalOpen
                        )
                    }
                >
                    {action.label}
                </Dropdown.Item>
            )
        }
    })

    return actions
}

export const hasPermission = (
    permissions: IUserPermission[] | null,
    permissionNames: USER_PERMISSIONS_NAMES[]
): boolean => {
    return !!permissions?.find(
        (permission) => permission.application === APP_NAMES.DIGITIZER && permissionNames?.includes(permission.name)
    )
}

const allowedJoistLinesDrawingTypes = new Set<DRAWABLE_TYPES>([
    DRAWABLE_TYPES.ROOF_SYSTEM,
    DRAWABLE_TYPES.ROOF_SYSTEMS,
    DRAWABLE_TYPES.FLOOR_SYSTEM,
    DRAWABLE_TYPES.FLOOR_SYSTEMS,
])

// Determines in Paper.js if a Paper item is a joist line that should be hidden by default
export const isNonAreaJoistLine = (item: paper.Item) => {
    if (!item.data?.drawing_type) return false

    return allowedJoistLinesDrawingTypes.has(item.data.drawing_type) && !item.fillColor
}

export const getCustomerName = (customers, currentCustomerId?: string | undefined): string => {
    return customers?.find((customer) => customer.id === currentCustomerId)?.name || ''
}

export const getCustomerLocation = (locations, currentLocationId): string => {
    return locations?.find((location) => location.id === currentLocationId)?.name || ''
}

export const getPackagesList = (packages: string[]): string => {
    return packages.join(', ')
}

/**
 * Return boolean value based on a list of project statuses when the flags and flag tool should be visible
 * @param projectStatusName
 */
export const isFlagsEnabledForProject = (_projectStatusName: PROJECT_STATUS_NAME): boolean => {
    return true

    /***
     * Commenting out the following code for the time being (2025-02-03 for BLUEPRINTS-15852).
     * If/When we decide to go back to allowing only for certain statuses we can uncomment it out.
     * Ops would like QRM flags available for ALL statuses for the time being.
     */

    // return isProjectStatusEnableFlags.has(projectStatusName)
}
